/**
 * Phase 281 — Context-Aware Email Document Dialog.
 *
 * Wraps the Phase 185.d `POST /api/documents/email-document` endpoint with a
 * single React modal that adapts its copy, placeholder hints, and default
 * personal message to the type of document being shared.
 *
 * Backend contract:
 *   doc_path: str            // e.g. "/payslips/abc/view" or "/fees/xyz/view"
 *   recipient_email: str     // required, validated server-side
 *   recipient_name?: str
 *   custom_message?: str
 *   subject?: str            // optional override
 *
 * Anti-abuse: server-side hourly rate limit + magic-link viewers cannot
 * trigger new emails (see _phase185d_email_document).
 *
 * Props
 *   open / onOpenChange      Dialog visibility (parent-controlled).
 *   documentType             'payslip' | 'invoice' | 'fee_receipt' | 'payslip_receipt'
 *   docPath                  Server-whitelisted document path. REQUIRED.
 *   defaultEmail             Pre-fills the Recipient Email input.
 *   defaultName              Pre-fills the Recipient Name input.
 *   contextData              { period, amount, ... } — used inside the
 *                            default personal message template.
 *
 * Templates and placeholders auto-derive from `documentType`.  The user can
 * still freely edit the message before clicking "Send Magic Link".
 */
import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Mail, Send, Clock, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const TEMPLATES = {
  payslip: {
    title: 'Email Payslip',
    subtitle: "Send a secure 7-day magic-link to your employee. They won't need an Out-Play login.",
    namePlaceholder: 'e.g. Prabhakar M',
    messageBuilder: ({ name, period }) =>
      `Hi ${name || 'there'}, please find attached your secure payslip for ${period || 'this month'}. Reach out if anything looks off. Thank you!`,
    defaultSubject: ({ period }) => `Your Payslip · ${period || ''}`.trim(),
    sendCta: 'Send Payslip',
  },
  payslip_receipt: {
    title: 'Email Payslip Receipt',
    subtitle: 'Send a secure 7-day magic-link to your employee.',
    namePlaceholder: 'e.g. Prabhakar M',
    messageBuilder: ({ name, period }) =>
      `Hi ${name || 'there'}, here is your payslip receipt for ${period || 'this month'}. Thanks for the great work!`,
    defaultSubject: ({ period }) => `Payslip Receipt · ${period || ''}`.trim(),
    sendCta: 'Send Receipt',
  },
  invoice: {
    title: 'Email Fee Invoice',
    subtitle: 'Send a secure 7-day magic-link to a parent, athlete, or accountant.',
    namePlaceholder: "e.g. Rohan's Father",
    messageBuilder: ({ name, period, amount }) => {
      const amt = amount ? ` (${amount})` : '';
      return `Hi ${name || 'there'}, here is the training fee invoice${amt} for ${period || 'this month'} — kindly settle at your earliest convenience. Thanks!`;
    },
    defaultSubject: ({ period }) => `Training Fee Invoice · ${period || ''}`.trim(),
    sendCta: 'Send Invoice',
  },
  fee_receipt: {
    title: 'Email Fee Receipt',
    subtitle: 'Send a secure 7-day magic-link confirming this payment.',
    namePlaceholder: "e.g. Rohan's Father",
    messageBuilder: ({ name, period, amount }) => {
      const amt = amount ? ` of ${amount}` : '';
      return `Hi ${name || 'there'}, attached is the payment receipt${amt} for ${period || 'this month'}. Thank you!`;
    },
    defaultSubject: ({ period }) => `Payment Receipt · ${period || ''}`.trim(),
    sendCta: 'Send Receipt',
  },
};

const DEFAULT_TEMPLATE = TEMPLATES.invoice;

export default function EmailDocumentDialog({
  open,
  onOpenChange,
  documentType = 'invoice',
  docPath,
  defaultEmail = '',
  defaultName = '',
  contextData = {},
}) {
  const tpl = TEMPLATES[documentType] || DEFAULT_TEMPLATE;

  const initialMessage = useMemo(
    () => tpl.messageBuilder({ name: defaultName, ...contextData }),
    // Rebuild whenever the parent re-opens the dialog with new context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentType, defaultName, contextData?.period, contextData?.amount],
  );

  const [recipientEmail, setRecipientEmail] = useState(defaultEmail);
  const [recipientName, setRecipientName] = useState(defaultName);
  const [customMessage, setCustomMessage] = useState(initialMessage);
  const [subject, setSubject] = useState(tpl.defaultSubject(contextData));
  const [sending, setSending] = useState(false);

  // When the dialog re-opens for a different row, re-seed the inputs from
  // the freshly-passed props.  Without this the inputs would stick to the
  // previous row's data when the parent reuses the same `<EmailDocumentDialog />`.
  useEffect(() => {
    if (!open) return;
    setRecipientEmail(defaultEmail);
    setRecipientName(defaultName);
    setCustomMessage(initialMessage);
    setSubject(tpl.defaultSubject(contextData));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultEmail, defaultName, initialMessage]);

  const handleSend = async () => {
    if (!docPath) {
      toast.error('Missing document path. Cannot send.');
      return;
    }
    const emailTrim = (recipientEmail || '').trim();
    if (!emailTrim.includes('@') || !emailTrim.includes('.')) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setSending(true);
    try {
      await api.post('/documents/email-document', {
        doc_path: docPath,
        recipient_email: emailTrim,
        recipient_name: (recipientName || '').trim(),
        custom_message: (customMessage || '').trim(),
        subject: (subject || '').trim(),
      });
      toast.success(`Magic link sent to ${emailTrim}`, {
        description: 'Link is valid for 7 days. The recipient does not need an Out-Play login.',
      });
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="email-document-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-indigo-600" />
            {tpl.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {tpl.subtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Recipient block */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Recipient Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="recipient@example.com"
                autoFocus={!defaultEmail}
                data-testid="email-document-recipient-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Recipient Name</Label>
              <Input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder={tpl.namePlaceholder}
                data-testid="email-document-recipient-name"
              />
            </div>
          </div>

          {/* Subject */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Subject</Label>
            <Input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject line"
              data-testid="email-document-subject"
            />
          </div>

          {/* Personal message — pre-populated, fully editable */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Personal Message</Label>
            <Textarea
              rows={5}
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Add a brief note for the recipient…"
              className="resize-none"
              data-testid="email-document-message"
            />
            <p className="text-[11px] text-muted-foreground">
              The actual document is delivered via a secure magic-link — never as a raw PDF attachment.
            </p>
          </div>

          {/* Security note */}
          <div className="flex items-start gap-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-900/40 p-3">
            <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
            <div className="text-[11px] leading-relaxed text-emerald-800 dark:text-emerald-200">
              <span className="font-semibold">Secure magic link</span> — opens directly to this document for 7 days,
              then expires automatically. No Out-Play account required.
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            data-testid="email-document-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSend}
            disabled={sending || !recipientEmail.trim()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="email-document-send"
          >
            {sending ? (
              <><Clock className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Sending…</>
            ) : (
              <><Send className="h-3.5 w-3.5 mr-1.5" /> {tpl.sendCta || 'Send Magic Link'}</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
